import { Action } from '@reduxjs/toolkit';

import { ActiveFleet, OpenedAgreementsStats, Revenue, Utilization } from 'services/resources';
import { FailureActionTypes } from 'services/utilityTypes';

const DASHBOARD_ACTION_BASE = 'app/services/Dashboard/';

export enum DashboardActionTypes {
  ACTIVE_FLEET_START_LOADING = DASHBOARD_ACTION_BASE + 'ACTIVE_FLEET_START_LOADING',
  REVENUE_START_LOADING = DASHBOARD_ACTION_BASE + 'REVENUE_START_LOADING',
  AGREEMENTS_STATS_START_LOADING = DASHBOARD_ACTION_BASE + 'AGREEMENTS_STATS_START_LOADING',
  UTILIZATION_DATA_START_LOADING = DASHBOARD_ACTION_BASE + 'UTILIZATION_DATA_START_LOADING',
  GET_ACTIVE_FLEET_SUCCESS = DASHBOARD_ACTION_BASE + 'GET_ACTIVE_FLEET_SUCCESS',
  GET_ACTIVE_FLEET_FAIL = DASHBOARD_ACTION_BASE + 'GET_ACTIVE_FLEET_FAIL',
  GET_REVENUE_SUCCESS = DASHBOARD_ACTION_BASE + 'GET_REVENUE_SUCCESS',
  GET_REVENUE_FAIL = DASHBOARD_ACTION_BASE + 'GET_REVENUE_FAIL',
  GET_AGREEMENTS_STATS_SUCCESS = DASHBOARD_ACTION_BASE + 'GET_AGREEMENTS_STATS_SUCCESS',
  GET_AGREEMENTS_STATS_FAIL = DASHBOARD_ACTION_BASE + 'GET_AGREEMENTS_STATS_FAIL',
  GET_UTILIZATION_DATA_SUCCESS = DASHBOARD_ACTION_BASE + 'GET_UTILIZATION_DATA_SUCCESS',
  GET_UTILIZATION_DATA_FAIL = DASHBOARD_ACTION_BASE + 'GET_UTILIZATION_DATA_FAIL',
}

type DashboardFailureActionTypes = FailureActionTypes<DashboardActionTypes>;

interface DashboardActionBase extends Action {
  type: DashboardActionTypes;
}

interface DashboardActionWithPayload extends Action {
  type: DashboardActionTypes;
  payload: Record<string, unknown>;
}

interface DashboardFailureAction extends DashboardActionBase {
  type: DashboardFailureActionTypes;
  payload: { errors: any };
}

interface ActiveFleetStartLoadingAction extends DashboardActionBase {
  type: DashboardActionTypes.ACTIVE_FLEET_START_LOADING;
}

interface RevenueStartLoadingAction extends DashboardActionBase {
  type: DashboardActionTypes.REVENUE_START_LOADING;
}

interface AgreementsStatsStartLoadingAction extends DashboardActionBase {
  type: DashboardActionTypes.AGREEMENTS_STATS_START_LOADING;
}

interface UtilizationDataStartLoadingAction extends DashboardActionBase {
  type: DashboardActionTypes.UTILIZATION_DATA_START_LOADING;
}

interface ActiveFleetGetAction extends DashboardActionWithPayload {
  type: DashboardActionTypes.GET_ACTIVE_FLEET_SUCCESS;
  payload: { activeFleet: ActiveFleet };
}

interface RevenueGetAction extends DashboardActionWithPayload {
  type: DashboardActionTypes.GET_REVENUE_SUCCESS;
  payload: { revenue: Revenue };
}

interface AgreementsStatsGetAction extends DashboardActionWithPayload {
  type: DashboardActionTypes.GET_AGREEMENTS_STATS_SUCCESS;
  payload: { agreements: OpenedAgreementsStats };
}

interface UtilizationDataGetAction extends DashboardActionWithPayload {
  type: DashboardActionTypes.GET_UTILIZATION_DATA_SUCCESS;
  payload: { utilization: Utilization };
}

export type DashboardAction =
  | ActiveFleetStartLoadingAction
  | RevenueStartLoadingAction
  | AgreementsStatsStartLoadingAction
  | UtilizationDataStartLoadingAction
  | ActiveFleetGetAction
  | RevenueGetAction
  | DashboardFailureAction
  | AgreementsStatsGetAction
  | UtilizationDataGetAction;

export const activeFleetStartLoading = (): ActiveFleetStartLoadingAction => ({
  type: DashboardActionTypes.ACTIVE_FLEET_START_LOADING,
});

export const receivedActiveFleetSuccess = (activeFleet: ActiveFleet): ActiveFleetGetAction => ({
  type: DashboardActionTypes.GET_ACTIVE_FLEET_SUCCESS,
  payload: { activeFleet },
});

export const receivedActiveFleetFail = (errors): DashboardFailureAction => ({
  type: DashboardActionTypes.GET_ACTIVE_FLEET_FAIL,
  payload: { errors },
});

export const revenueStartLoading = (): RevenueStartLoadingAction => ({
  type: DashboardActionTypes.REVENUE_START_LOADING,
});

export const receivedRevenueSuccess = (revenue: Revenue): RevenueGetAction => ({
  type: DashboardActionTypes.GET_REVENUE_SUCCESS,
  payload: { revenue },
});

export const receivedRevenueFail = (errors): DashboardFailureAction => ({
  type: DashboardActionTypes.GET_REVENUE_FAIL,
  payload: { errors },
});

export const agreementsStatsStartLoading = (): AgreementsStatsStartLoadingAction => ({
  type: DashboardActionTypes.AGREEMENTS_STATS_START_LOADING,
});

export const receivedAgreementsStatsSuccess = (agreements: OpenedAgreementsStats): AgreementsStatsGetAction => ({
  type: DashboardActionTypes.GET_AGREEMENTS_STATS_SUCCESS,
  payload: { agreements },
});

export const receivedAgreementsStatsFail = (errors): DashboardFailureAction => ({
  type: DashboardActionTypes.GET_AGREEMENTS_STATS_FAIL,
  payload: { errors },
});

export const utilizationDataStartLoading = (): UtilizationDataStartLoadingAction => ({
  type: DashboardActionTypes.UTILIZATION_DATA_START_LOADING,
});

export const receivedUtilizationDataSuccess = (utilization: Utilization): UtilizationDataGetAction => ({
  type: DashboardActionTypes.GET_UTILIZATION_DATA_SUCCESS,
  payload: { utilization },
});

export const receivedUtilizationDataFail = (errors): DashboardFailureAction => ({
  type: DashboardActionTypes.GET_UTILIZATION_DATA_FAIL,
  payload: { errors },
});
